/**
 * Toolbar pill chrome shared by SortPill and FilterPill, so "Sorted by"
 * and "Filtered by" read as one pair: same height as the toolbar icon
 * group, same border, label in muted text and the value in bold.
 */
import { styled } from '@mui/material/styles';
// Defines the `qhs-pop-in` keyframes the panel animation uses.
import '@shared/ui/Popover/Popover.css';

export const PillRoot = styled('div')({
  position: 'relative',
  display: 'inline-flex',
});

export const PillTrigger = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  // Explicit 36-px height so the pill lines up with the adjacent
  // toolbar icon group (TableToolbar is 2-px padding + 32-px buttons
  // = 36 px); previously we had vertical padding-only sizing which
  // rendered the trigger visibly shorter than the icons beside it.
  height: 36,
  padding: '0 12px',
  background: 'var(--qhs-surface)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 8,
  fontFamily: 'inherit',
  fontSize: 12,
  color: 'var(--qhs-text-2)',
  cursor: 'pointer',
  transition: 'border-color 120ms ease, background-color 120ms ease',
  '&:hover': {
    borderColor: 'var(--qhs-border-strong)',
    background: 'var(--qhs-surface-hover)',
  },
  '&:focus-visible': {
    outline: '2px solid var(--qhs-brand)',
    outlineOffset: 1,
  },
  '& svg': { width: 12, height: 12 },
});

export const PillLabel = styled('span')({ color: 'var(--qhs-text-3)' });
// `$tone`: 'brand' marks a value that changes what the table shows (an
// active filter); 'muted' is the "None" placeholder.
const VALUE_COLORS = { brand: 'var(--qhs-brand)', muted: 'var(--qhs-text-3)' };

export const PillValue = styled('span', {
  shouldForwardProp: prop => prop !== '$tone',
})(({ $tone }) => ({
  color: VALUE_COLORS[$tone] || 'var(--qhs-text-1)',
  fontWeight: $tone === 'muted' ? 400 : 600,
  maxWidth: 180,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const PillPanel = styled('div')({
  position: 'absolute',
  top: 'calc(100% + 6px)',
  right: 0,
  zIndex: 40,
  width: 300,
  background: 'var(--qhs-surface)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 10,
  boxShadow: 'var(--qhs-sh-3)',
  padding: 12,
  // Same entrance as the design-system Popover (the Columns and Density
  // menus beside these pills), so every toolbar menu opens alike.
  animation: 'qhs-pop-in 150ms var(--qhs-ease-out)',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

export const PillPanelHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 10,
});

export const PillPanelTitle = styled('div')({
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--qhs-text-1)',
});
